import { Badge, Box, ButtonBase } from '@mui/material'
import type { ReactNode } from 'react'
import type { NavigationItem } from '../navigation.types'

export type NavigationRailProps = {
  activeItemId?: string | null
  ariaLabel?: string
  bottomItems?: NavigationItem[]
  logo?: ReactNode
  onItemSelect?: (id: string) => void
  topItems: NavigationItem[]
  width?: number | string
}

const railBackground = '#434a51'
const railBorder = railBackground
const itemHover = 'rgba(255, 255, 255, 0.05)'
const itemActive = 'rgba(0, 0, 0, 0.18)'
const itemFocus = 'rgba(33, 152, 243, 0.35)'

function renderRailItem(item: NavigationItem, activeItemId?: string | null, onItemSelect?: (id: string) => void) {
  const selected = item.id === activeItemId

  return (
    <ButtonBase
      key={item.id}
      aria-current={selected ? 'page' : undefined}
      aria-label={item.ariaLabel ?? item.label}
      disabled={item.disabled}
      onClick={() => onItemSelect?.(item.id)}
      sx={{
        alignItems: 'center',
        borderRadius: 0,
        color: item.disabled ? 'rgba(255, 255, 255, 0.3)' : '#f8f9fa',
        display: 'flex',
        height: 52,
        justifyContent: 'center',
        position: 'relative',
        transition: 'background-color 150ms ease, color 150ms ease, box-shadow 150ms ease',
        width: '100%',
        '&:hover': {
          backgroundColor: item.disabled ? 'transparent' : itemHover,
        },
        '&:focus-visible': {
          boxShadow: `inset 0 0 0 3px ${itemFocus}`,
          outline: 'none',
        },
        '&::before': {
          backgroundColor: 'transparent',
          content: '""',
          left: 0,
          position: 'absolute',
          top: 0,
          bottom: 0,
          // width: 3,
        },
        ...(selected && {
          backgroundColor: itemActive,
          color: '#ffffff',
          '&::before': {
            backgroundColor: 'primary.main',
          },
        }),
      }}
    >
      {item.badgeContent ? (
        <Badge badgeContent={item.badgeContent} color="error">
          <Box aria-hidden="true" sx={{ display: 'flex', fontSize: 22, lineHeight: 1 }}>
            {item.icon}
          </Box>
        </Badge>
      ) : (
        <Box aria-hidden="true" sx={{ display: 'flex', fontSize: 22, lineHeight: 1 }}>
          {item.icon}
        </Box>
      )}
      <Box
        component="span"
        sx={{
          border: 0,
          clip: 'rect(0 0 0 0)',
          height: 1,
          margin: -1,
          overflow: 'hidden',
          p: 0,
          position: 'absolute',
          whiteSpace: 'nowrap',
          width: 1,
        }}
      >
        {item.label}
      </Box>
    </ButtonBase>
  )
}

export function NavigationRail({
  activeItemId = null,
  ariaLabel = 'Primary navigation',
  bottomItems = [],
  logo,
  onItemSelect,
  topItems,
  width = 72,
}: NavigationRailProps) {
  return (
    <Box
      aria-label={ariaLabel}
      component="nav"
      sx={{
        backgroundColor: railBackground,
        borderRight: `1px solid ${railBorder}`,
        color: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        justifyContent: 'space-between',
        minHeight: '100%',
        py: 1,
        width,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {logo ? (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center',
              minHeight: 56,
              px: 1,
            }}
          >
            {logo}
          </Box>
        ) : null}
        <Box role="group" sx={{ display: 'flex', flexDirection: 'column' }}>
          {topItems.map(item => renderRailItem(item, activeItemId, onItemSelect))}
        </Box>
      </Box>

      {bottomItems.length > 0 ? (
        <Box role="group" sx={{ display: 'flex', flexDirection: 'column' }}>
          {bottomItems.map(item => renderRailItem(item, activeItemId, onItemSelect))}
        </Box>
      ) : null}
    </Box>
  )
}
